import React, { Component } from 'react';
import FormicaLabel from '../../../custom/FormicaLabel';
import FormicaInput from '../../../custom/FormicaInput';
import FormicaSearchSelectBox, { FormicaSelectBoxItem } from '../../../custom/FormicaSearchSelectBox';
import VelocityVariableTestComponent from './VelocityVariableTestComponent';
import VelocityVariableSelectComponent from './VelocityVariableSelectComponent';
import VelocityVariableGroupByComponent from './VelocityVariableGroupByComponent';
import VelocityVariableWhereComponent from './VelocityVariableWhereComponent';
import { TransactionJsonModel } from '../../../../domain/stream/transaction/TransactionDomains';
import { RootState } from '../../../../redux/store';
import { VelocityVariableState } from '../../../../redux/reducers/VelocityVariableReducer';
import { enterComponent, leaveComponent } from '../../../../redux/actions/ComponentActions';
import {
  changeVelocityVariable,
  changeVelocityVariableIsUpdate,
  changeVelocityVariablePermittedStores,
} from '../../../../redux/actions/VelocityVariableActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../../../constant/CollectionNames';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import CustomLoading from '../../../custom/CustomLoading';
import RoleHelper from '../../../../RoleHelper';
import { StreamJsonModel } from '../../../../domain/stream/stream/StreamDomain';
import LanguageUtil from '../../../../util/LanguageUtil';
import ComponentName from '../../../../constant/ComponentName';
import GatewayContainer from '../../../../config/GatewayContainer';
import GatewayNames from '../../../../constant/GatewayNames';
import { StreamClient } from '../../../../client/stream/StreamClient';
import NavigationUtil from '../../../../util/NavigationUtil';

class VelocityVariableEditorComponent extends Component<Props, State> {
  public state = new State();

  private transactionSubscription: subscribe.Subscription | undefined;
  private streamSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly storeClient: StreamClient = GatewayContainer.getInstance().get(GatewayNames.STREAM_CLIENT);
  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const {
      velocityVariable: { velocityVariable },
      updated,
    } = this.props;
    const { update, loading } = this.state;
    if (loading) return <CustomLoading />;
    return (
      <div className={'grid grid-cols-5 w-full h-full'}>
        <div className={'col-span-1 flex flex-col border-r border-f-black-5 py-5 px-5  gap-y-2'}>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.channel', defaultMessage: 'Channel' })} />
            <FormicaSearchSelectBox
              options={this.getTransactionOptions()}
              disabled={!RoleHelper.canEdit('EnrichmentVelocity')}
              onChange={(e: FormicaSelectBoxItem) => this.onChangeTransaction(e)}
              value={velocityVariable.transaction}
              rounded
              data-testid={`${ComponentName.VELOCITY}_channel_select`}
            />
          </div>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.store', defaultMessage: 'Store' })} />
            <FormicaSearchSelectBox
              options={this.getStreamOptions()}
              disabled={!RoleHelper.canEdit('EnrichmentVelocity')}
              onChange={(e: FormicaSelectBoxItem) => this.onChangeStream(e)}
              value={
                velocityVariable.tenant && velocityVariable.tenant != NavigationUtil.getTenant()
                  ? `${velocityVariable.tenant}:${velocityVariable.stream}`
                  : velocityVariable.stream
              }
              rounded
              data-testid={`${ComponentName.VELOCITY}_store_select`}
            />
          </div>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.skip', defaultMessage: 'Skip' })} />
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentVelocity')}
              type={'number'}
              value={velocityVariable.skip}
              onChange={(e) => this.onChangeSkip(e)}
              data-testid={`${ComponentName.VELOCITY}_skip_input`}
            />
          </div>
          <div className={'flex flex-col'}>
            <FormicaLabel label={LanguageUtil.get({ id: 'common.label.limit', defaultMessage: 'Limit' })} />
            <FormicaInput
              disabled={!RoleHelper.canEdit('EnrichmentVelocity')}
              type={'number'}
              value={velocityVariable.limit}
              onChange={(e) => this.onChangeLimit(e)}
              data-testid={`${ComponentName.VELOCITY}_limit_input`}
            />
          </div>
        </div>
        <div className={'col-span-4 flex flex-col w-full h-full px-5 py-5 gap-y-2 overflow-y-scroll dark:bg-slate-700 max-h-[calc(100vh-270px)]'}>
          <VelocityVariableTestComponent />
          <div className={'flex flex-col w-full h-full gap-y-3 mt-3'}>
            {velocityVariable.function != 'count' && (
              <VelocityVariableSelectComponent disabled={this.isExpressionDisabled()} update={update} velocityUpdated={updated} />
            )}
            <VelocityVariableGroupByComponent disabled={this.isExpressionDisabled()} update={update} velocityUpdated={updated} />
            <VelocityVariableWhereComponent disabled={this.isExpressionDisabled()} update={update} velocityUpdated={updated} />
          </div>
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    await this.startTracker();
  }

  public async componentWillUnmount() {
    await this.stopTracker();
  }

  private startTracker = async () => {
    const transactionQuery = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS);
    const streamQuery = query.QueryBuilder.select().from(CollectionNames.STREAMS);
    this.transactionSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, transactionQuery.build());
    this.streamSubscription = subscribe.Formica.subscribe(CollectionNames.STREAMS, streamQuery.build());
    this.fetchPermittedTenantStores();
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.transactionSubscription, this.streamSubscription]);
  };

  private tracker = async () => {
    if (this.transactionSubscription?.ready() && this.streamSubscription?.ready()) {
      const transactionQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build();
      const streamQuery: query.Query = query.QueryBuilder.select().from(CollectionNames.STREAMS).build();
      const transactions: TransactionJsonModel[] = await this.repository.findMany(transactionQuery);
      const streams: any[] = await this.repository.findMany(streamQuery);
      this.setState({ loading: false, transactions, streams });
    }
  };

  private stopTracker = async () => {
    await this.transactionSubscription?.stop();
    await this.streamSubscription?.stop();
    this.computation?.stop();
  };

  private onChangeTransaction = (e: FormicaSelectBoxItem) => {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    velocityVariable.transaction = e.value;
    changeVelocityVariable(velocityVariable);
    this.setState({ update: this.state.update + 1 });
  };

  private onChangeStream = (e: FormicaSelectBoxItem) => {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    const [tenant, stream] = e.value.split(':');
    if (tenant != undefined && stream != undefined && tenant.length > 0 && stream.length > 0) {
      velocityVariable.tenant = tenant;
      velocityVariable.stream = stream;
    } else {
      velocityVariable.tenant = NavigationUtil.getTenant();
      velocityVariable.stream = e.value;
    }
    changeVelocityVariable(velocityVariable);
    this.setState({ update: this.state.update + 1 });
  };

  private onChangeSkip = (e: any) => {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    velocityVariable.skip = e.target.value;
    changeVelocityVariable(velocityVariable);
  };

  private onChangeLimit = (e: any) => {
    const {
      velocityVariable: { velocityVariable },
      changeVelocityVariable,
    } = this.props;
    velocityVariable.limit = e.target.value;
    changeVelocityVariable(velocityVariable);
  };

  private getTransactionOptions = (): FormicaSelectBoxItem[] => {
    const { transactions } = this.state;
    return transactions.map((transaction) => {
      return { label: transaction.name, value: transaction.name };
    });
  };

  private getStreamOptions = (): FormicaSelectBoxItem[] => {
    const { streams } = this.state;
    const { velocityVariable } = this.props;
    const defaultStreams = streams.map((stream) => {
      return { label: stream.name, value: stream.name };
    });
    Object.entries(velocityVariable.permittedTenantStores).forEach(([tenant, streams]) => {
      streams.forEach((stream) => {
        defaultStreams.push({
          label: `[${tenant}]: ${stream.name}`,
          value: `${tenant}:${stream.name}`,
        });
      });
    });
    return defaultStreams;
  };

  private isExpressionDisabled = () => {
    const {
      velocityVariable: { velocityVariable },
    } = this.props;
    return (
      velocityVariable.stream == undefined ||
      velocityVariable.transaction == undefined ||
      velocityVariable.stream.length == 0 ||
      velocityVariable.transaction.length == 0
    );
  };

  private fetchPermittedTenantStores = async () => {
    const { changeVelocityVariablePermittedStores } = this.props;
    const stores = await this.storeClient.getPermittedStores();
    changeVelocityVariablePermittedStores(stores);
  };
}

interface Props extends ReduxProps {
  updated: number;
}

class State {
  transactions: TransactionJsonModel[] = [];
  streams: StreamJsonModel[] = [];
  loading: boolean = true;
  update: number = 0;
}

const mapStateToProps = (state: RootState) => ({
  velocityVariable: state.velocityVariable as VelocityVariableState,
});

const mapDispatchToProps = {
  enterComponent,
  leaveComponent,
  changeVelocityVariable,
  clearVelocityVariable: changeVelocityVariableIsUpdate,
  changeVelocityVariablePermittedStores,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export default connector(VelocityVariableEditorComponent);
